import { useEffect, useState } from 'react';
import postRobot from 'post-robot';

import { analyticsClickedSmartpoolingRecommendations, analyticsSmartPoolingResults } from '../api/services/Analytics';
import { findSuggestions } from '../api/services/Rides';

import { useTimezone } from './useTimezone';

interface UseSmartPoolingHook {
  hasSmartPoolingRequirements: boolean;
  handleSearchRideSuggestions: () => Promise<void>;
  isSearching: boolean;
  disableSearch: boolean;
  suggestions: Suggestion[];
  handleSuggestionClick: (rideSlug: string, analyticsData: { index: number; riderCount: number }) => void;
}

const defaultSuggestion: Suggestion = {
  slug: '',
  anchor: 'drop_off',
  anchorTime: '',
  riderCount: 0,
  duration: 0,
  riders: [],
};

export const useSmartPooling = (
  formData?: Partial<RideFormData> | null,
  ridersForSmartPooling?: RiderForSmartPooling[]
): UseSmartPoolingHook => {
  const { mergeDateTime } = useTimezone();
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [disableSearch, setDisableSearch] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([
    defaultSuggestion,
    defaultSuggestion,
    defaultSuggestion,
  ]);

  const numberOfRiders = new Set(
    formData?.waypointsAttributes?.flatMap((waypoint) => waypoint.passengerIds).filter((id) => id) || []
  ).size;

  const numberOfRepeatingDays = new Set(
    ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].filter(
      (day) => formData?.tripTemplateAttributes?.[day] === true
    ) || []
  ).size;

  // check if all Smart Pooling conditions are met
  const hasSmartPoolingRequirements = !!(
    formData &&
    formData.tripTemplateAttributes?.startDate &&
    formData.tripTemplateAttributes?.endDate &&
    numberOfRepeatingDays > 0 &&
    numberOfRiders > 0 &&
    numberOfRiders <= 3 &&
    formData.waypointsAttributes?.length === 2 &&
    formData.waypointsAttributes?.every((waypoint) => waypoint.location?.placeId)
  );

  const handleSearchRideSuggestions = async () => {
    setIsSearching(true);
    if (formData) {
      const { time, tripTemplateAttributes, calendarSlug, waypointsAttributes, ...rest } = formData;
      const formattedAnchorTime = mergeDateTime(tripTemplateAttributes?.startDate as Date, time);

      const rideFormattedData = {
        tripTemplateAttributes: {
          ...tripTemplateAttributes,
          startDate: formattedAnchorTime,
          endDate: mergeDateTime(tripTemplateAttributes?.endDate as Date, '23:59:59'),
        },
        waypointsAttributes: waypointsAttributes?.map(({ location, passengerIds, anchor }) => ({
          anchor,
          location,
          passengerIds: passengerIds,
        })),
        ...(calendarSlug && { calendarSlug: calendarSlug }),
        ...rest,
      } as RideFormType;

      const response = await findSuggestions(rideFormattedData);

      if (response.type === 'success') {
        setDisableSearch(true);
        setSuggestions(response.data.suggestions);
        analyticsSmartPoolingResults(response.data.suggestions.length);
      }
    }
    setIsSearching(false);
  };

  const handleSuggestionClick = (slug: string, analyticsData: { index: number; riderCount: number }) => {
    const appUrl = window.location.origin;
    const rideIQClient = window.open(`${appUrl}/rides/${slug}/edit`);

    postRobot.once('getSmartPoolingData', { window: rideIQClient, domain: appUrl }, () => {
      return { waypointsAttributes: formData?.waypointsAttributes, riders: ridersForSmartPooling };
    });
    analyticsClickedSmartpoolingRecommendations(analyticsData.index, analyticsData.riderCount);
  };

  useEffect(() => {
    !suggestions.length && setSuggestions([defaultSuggestion, defaultSuggestion, defaultSuggestion]);

    setDisableSearch(false);
  }, [
    formData?.time,
    formData?.tripTemplateAttributes?.startDate,
    formData?.tripTemplateAttributes?.endDate,
    formData?.timeAnchor,
    numberOfRiders,
    numberOfRepeatingDays,
    formData?.waypointsAttributes?.every((waypoint) => waypoint.location?.placeId),
  ]);

  return {
    hasSmartPoolingRequirements,
    handleSearchRideSuggestions,
    isSearching,
    disableSearch: hasSmartPoolingRequirements && !isSearching ? disableSearch : true,
    suggestions,
    handleSuggestionClick,
  };
};
